import React, {useState} from "react";
import { useHistory } from "react-router-dom";
import {Accordion, AccordionItem, Button, Icon, Input, Tab, TabBar, TabsProvider} from "../../components";
import {
    ClientRelationshipGroup as ClientRelationshipGroupModel,
    ClientRelationshipGroupPartner
} from "../models/ClientRelationshipGroupHierarchy";
import {partnerApiClient} from "../PartnerApiClient";
import {App_Conf} from "../../core/app_conf";
import {DISPLAY_DATE_FORMAT, ISO8601_DATE_FORMAT} from "../../constants/common";
import {calculateAgeFromBirthdate, toDisplayDateFormat} from "../../utils/dateUtils";
import {useAppDispatch} from "../../store/hooks";
import {setClientRelationshipGroupInfoState} from "./ClientRelationshipGroupInfoSlice";

type ClientRelationshipGroupHeaderRowProps = {
    expanded: boolean;
    clientRelationshipGroup: ClientRelationshipGroupModel;
    isClientRelationshipGroupLocked: boolean;
};

type ClientRelationshipGroupDetailsProps = {
    clientRelationshipGroup: ClientRelationshipGroupModel;
    partners: ClientRelationshipGroupPartner[];
    isClientRelationshipGroupLocked: boolean;
};

const formatBirthDate = (birthDate: string | null) => {
    const formatted = toDisplayDateFormat(birthDate, DISPLAY_DATE_FORMAT);
    if (!formatted || formatted === "Invalid date") {
        return "-";
    }

    return formatted;
};
const formatPartnerField = (value: string | null) => {
    if (value === null || value.trim() === "") {
        return "-";
    }

    return value;
};

const calculateAge = (birthDate: string | null) => {
    if (!birthDate) {
        return "-";
    }

    const age = calculateAgeFromBirthdate(birthDate, ISO8601_DATE_FORMAT);
    if (!Number.isFinite(age)) {
        return "-";
    }

    return String(age);
};

const formatGender = (gender: string | null | undefined) => {
    if (gender === "M") {
        return "Male";
    }
    if (gender === "F") {
        return "Female";
    }
    return "-";
};

const formatClientNames = (clientRelationshipGroup: ClientRelationshipGroupModel) => {
    const names = clientRelationshipGroup.parties
        .map((party) => party.preferredName ?? party.wmFullLegalEntityName)
        .filter(Boolean)
        .join(", ");

    return names.length === 0 ? "-" : names;
};

const formatClientNamesWithPrimaryIcon = (clientRelationshipGroup: ClientRelationshipGroupModel) => {
    const names = clientRelationshipGroup.parties
        .map((party) => party.preferredName ?? party.wmFullLegalEntityName)
        .filter(Boolean);

    if (names.length === 0) {
        return "-";
    }

    return (
        <span>
            {names.map((name, index) => (
                <span key={`${name}-${index}`}>
                    {name}
                    {index === 0 ? (
                        <i aria-hidden="true" className="icon dds-icons planning-link-off-icon">link_off</i>
                    ) : null}
                    {index < names.length - 1 ? ", " : ""}
                </span>
            ))}
        </span>
    );
};

const formatUleks = (clientRelationshipGroup: ClientRelationshipGroupModel) => {
    const ulekList = clientRelationshipGroup.parties.map((party) => party.ulek).join(", ");

    return ulekList.length === 0 ? "-" : ulekList;
};

const formatStatus = (isClientRelationshipGroupLocked: boolean) =>
    isClientRelationshipGroupLocked ? (
        <span className="planning-status">
            <Icon name="lock" />{" "}
            Connected to GPS Profile
        </span>
    ) : (
        ""
    );

const formatClientRelationshipGroupName = (clientRelationshipGroup: ClientRelationshipGroupModel) => {
    const name = clientRelationshipGroup.name?.trim();

    if (!name) {
        return (
            <a
                className="planning-create-link"
                href={App_Conf.PWS_URL}
                rel="noopener noreferrer"
                target="_blank"
            >
                Create Client Relationship Group <i className="icon dds-icons">launch</i>
            </a>
        );
    }

    return name;
};

const isLinkClientRelationshipGroupDisabled = (
    clientRelationshipGroup: ClientRelationshipGroupModel,
    isClientRelationshipGroupLocked: boolean
) => {
    const hasName = Boolean(clientRelationshipGroup.name?.trim());
    const hasCrgid = Boolean(clientRelationshipGroup.crgid?.trim());

    return isClientRelationshipGroupLocked || !hasName || !hasCrgid;
};

const ClientRelationshipGroupHeaderRow = ({expanded, clientRelationshipGroup, isClientRelationshipGroupLocked}: ClientRelationshipGroupHeaderRowProps) => (
    <div className="planning-results-grid planning-results-row">
        <span className="planning-results-cell planning-results-title">
            <Icon name={expanded ? "chevron_down" : "chevron_right"} />
            {formatClientRelationshipGroupName(clientRelationshipGroup)}
        </span>
        <span className="planning-results-cell">
            {clientRelationshipGroup.name?.trim()
                ? formatClientNames(clientRelationshipGroup)
                : formatClientNamesWithPrimaryIcon(clientRelationshipGroup)}
        </span>
        <span className="planning-results-cell">{formatUleks(clientRelationshipGroup)}</span>
        <span className="planning-results-cell">{formatStatus(isClientRelationshipGroupLocked)}</span>
    </div>
);

const ClientRelationshipGroupDetails = ({clientRelationshipGroup, partners, isClientRelationshipGroupLocked}: ClientRelationshipGroupDetailsProps) => {
    const [showMembers, setShowMembers] = useState(true);
    const members = clientRelationshipGroup.parties;
    const history = useHistory();
    const isLinkDisabled = isLinkClientRelationshipGroupDisabled(clientRelationshipGroup, isClientRelationshipGroupLocked);

    return (
        <div className="planning-results-details">
            <TabsProvider tabIdArray={["planningMembersTab", "serviceTeamTab"]}>
                <TabBar size="medium">
                    <Tab
                        name="Client Relationship Group Members"
                        onClick={() => {
                            setShowMembers(true);
                        }}
                    />
                    <Tab
                        name="Service Team"
                        onClick={() => {
                            setShowMembers(false);
                        }}
                    />
                </TabBar>
            </TabsProvider>
            {showMembers ? (
                <>
                    <div className="planning-details-helper">
                        Client Relationship Group Members are managed within Partner Workstation.
                    </div>
                    <div className="planning-members-table">
                        <div className="planning-members-row planning-members-header">
                            <span>Full Name</span>
                            <span>ULEK</span>
                            <span>Birthdate</span>
                            <span>Age</span>
                            <span>Gender</span>
                            <span className="planning-members-menu-header" />
                        </div>
                        <hr className="planning-thematic" />
                        {members.length === 0 ? (
                            <div className="planning-members-empty">-</div>
                        ) : (
                            members.map((party) => (
                                <div
                                    className="planning-members-row"
                                    key={`${party.ulek}-${party.wmFullLegalEntityName}`}
                                >
                                    <span>{party.wmFullLegalEntityName}</span>
                                    <span>{party.ulek}</span>
                                    <span>{formatBirthDate(party.birthDate)}</span>
                                    <span>{calculateAge(party.birthDate)}</span>
                                    <span>{formatGender(party.gender)}</span>
                                    <span className="planning-members-menu-cell">
                                        <Button
                                            ariaLabel={`Planning member options for ${party.wmFullLegalEntityName}`}
                                            icon="only"
                                            iconName="menu_dots"
                                            kind="borderless"
                                            onClick={() => undefined}
                                            size="small"
                                            type="button"
                                        />
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                    <div className="planning-members-actions">
                        <Button
                            icon="none"
                            id="link_planning_group_button"
                            kind="primary"
                            onClick={() => {
                                history.push(`/ClientRelationshipGroup/Profile/Client/New/${clientRelationshipGroup.crgid}`);
                            }}
                            disabled={isLinkDisabled}
                            size="medium"
                            tabIndex={0}
                            type="button"
                        >
                            Link Client Relationship Group
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    <div className="planning-details-helper">
                        Service Team partner access is managed within Partner Workstation.
                    </div>
                    <div className="planning-members-table">
                        <div className="planning-service-team-row planning-service-team-header">
                            <span>Partner Name</span>
                            <span>Phone Number</span>
                            <span>Email Address</span>
                            <span>Partner Title / Role</span>
                        </div>
                        <hr className="planning-thematic" />
                        {partners.length === 0 ? (
                            <div className="planning-members-empty">-</div>
                        ) : (
                            partners.map((partner, index) => (
                                <div
                                    className="planning-service-team-row"
                                    key={`${partner.partnerCode}-${partner.partnerName}-${index}`}
                                >
                                    <span>{formatPartnerField(partner.partnerName)}</span>
                                    <span>{formatPartnerField(partner.phoneNumber)}</span>
                                    <span>{formatPartnerField(partner.email)}</span>
                                    <span>{formatPartnerField(partner.title)}</span>
                                </div>
                            ))
                        )}
                    </div>
                    <div className="planning-members-actions">
                        <Button
                            icon="none"
                            id="link_planning_group_service_team_button"
                            kind="primary"
                            onClick={() => {
                                history.push(`/ClientRelationshipGroup/Profile/Client/New/${clientRelationshipGroup.crgid}`);
                            }}
                            disabled={isLinkDisabled}
                            size="medium"
                            tabIndex={0}
                            type="button"
                        >
                            Link Client Relationship Group
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

const ClientRelationshipGroup: React.FC = () => {
    const history = useHistory();
    const [clientRelationshipGroups, setClientRelationshipGroups] = useState<ClientRelationshipGroupModel[]>([]);
    const [ulekInput, setUlekInput] = useState("");
    const [hasSearched, setHasSearched] = useState(false);
    const [showNoResults, setShowNoResults] = useState(false);
    const [isClientRelationshipGroupLocked, setIsClientRelationshipGroupLocked] = useState(false);
    const [partners, setPartners] = useState<ClientRelationshipGroupPartner[]>([]);
    const dispatch = useAppDispatch();

    const noResultsTitle = "No matching records found.";
    const defaultNoResultsDetail = {
        line1: "Please verify that the client information you are searching for exists and that you have access",
        line2: "within Partner Workstation. You can only match client data that you have access to."
    };
    const emptySearchDetail = {
        line1: "Please enter search criteria and try again.",
        line2: ""
    };
    const [noResultsDetail, setNoResultsDetail] = useState(defaultNoResultsDetail);

    const handleSearchClientRelationshipGroups = async () => {
        const trimmedUlek = ulekInput.trim();
        setHasSearched(true);
        setClientRelationshipGroups([]);
        setShowNoResults(false);
        setIsClientRelationshipGroupLocked(false);
        setPartners([]);
        setNoResultsDetail(defaultNoResultsDetail);

        if (!trimmedUlek) {
            setNoResultsDetail(emptySearchDetail);
            setShowNoResults(true);
            return;
        }

        try {
            const response = await partnerApiClient.getClientRelationshipGroups(trimmedUlek);
            const results = response.clientRelationshipGroups ?? [];

            dispatch(setClientRelationshipGroupInfoState({...response,ulek:trimmedUlek}))

            setIsClientRelationshipGroupLocked(Boolean(response.isClientRelationshipGroupLocked));
            setPartners(response.partners ?? []);

            if (results.length === 0) {
                setNoResultsDetail(defaultNoResultsDetail);
                setShowNoResults(true);
            } else {
                setClientRelationshipGroups(results);
            }
        } catch (_error) {
            setNoResultsDetail(defaultNoResultsDetail);
            setShowNoResults(true);
        }
    };

    const handleClearClientRelationshipGroups = () => {
        setClientRelationshipGroups([]);
        setUlekInput("");
        setShowNoResults(false);
        setHasSearched(false);
        setIsClientRelationshipGroupLocked(false);
        setPartners([]);
    };

    const handleCancel = () => {
        history.goBack();
    };

    const noResultsContent = hasSearched && showNoResults ? (
        <div className="planning-search-results-helper">
            <div>{noResultsTitle}</div>
            <div className="planning-search-results-helper-detail">
                {noResultsDetail.line1}
                {noResultsDetail.line2 ? (
                    <>
                        <br />
                        {noResultsDetail.line2}
                    </>
                ) : null}
            </div>
        </div>
    ) : (
        <div className="planning-search-results-helper">
            Search for a Client Relationship Group using the fields to the left
        </div>
    );

    return (
        <div className={"planning-page-container"}>
            <div className={"planning-page-header"}>
                <div className={"planning-page-header-row"}>
                    <span className="font-xxl planning-page-title">Select Matching Client Relationship Group</span>
                    <Button
                        className="planning-cancel-button"
                        icon="none"
                        iconName=""
                        kind="secondary"
                        onClick={handleCancel}
                        size="small"
                        type="button"
                    >
                        Cancel
                    </Button>
                </div>
                <p className="planning-page-subtitle">Search for a Client Relationship Group, Client name, or ULEK below. Clients must be added to a Client <br /> Relationship Group in Partner Workstation to be linked to a GPS profile.</p>
            </div>
            <div className={"planning-page-search-result"}>
                <div className={"search-fields-container"}>
                    <div className="planning-field-row">
                        <label htmlFor="planning-group-name">Client Relationship Group</label>
                        <Input
                            className="planning-input-box"
                            id="planning-group-name"
                            name="planning-group-name"
                            aria-label="planning-group-name"
                            type="text"
                            disabled
                        />
                    </div>
                    <div className="planning-field-row">
                        <label htmlFor="planning-first-name">First Name</label>
                        <Input
                            className="planning-input-box"
                            id="planning-first-name"
                            name="planning-first-name"
                            aria-label="planning-first-name"
                            type="text"
                            disabled
                        />
                    </div>
                    <div className="planning-field-row">
                        <label htmlFor="planning-last-name">Last Name</label>
                        <Input
                            className="planning-input-box"
                            id="planning-last-name"
                            name="planning-last-name"
                            aria-label="planning-last-name"
                            type="text"
                            disabled
                        />
                    </div>
                    <div className="planning-field-row">
                        <label htmlFor="planning-ulek">ULEK</label>
                        <Input
                            className="planning-input-box"
                            id="planning-ulek"
                            name="planning-ulek"
                            aria-label="planning-ulek"
                            type="text"
                            value={ulekInput}
                            onChange={(event) => setUlekInput(event.target.value)}
                        />
                    </div>
                    <div className="planning-page-actions">
                        <Button
                            className="marginright-md"
                            icon="none"
                            id="clear_planning_group_button"
                            onClick={handleClearClientRelationshipGroups}
                            size="medium"
                            tabIndex={0}
                            kind="borderless"
                            type="button"
                        >
                            Clear
                        </Button>
                        <Button
                            icon="none"
                            id="search_planning_group_button"
                            kind="primary"
                            onClick={handleSearchClientRelationshipGroups}
                            size="medium"
                            tabIndex={0}
                            type="button"
                        >
                            Search Client Relationship Groups
                        </Button>
                    </div>
                </div>
                <div className="search-results-container">
                    <div>
                        <div className="planning-search-results-header">
                            Search Results {clientRelationshipGroups.length > 0 ? `(${clientRelationshipGroups.length})` : ""}
                        </div>
                        <hr className="planning-thematic" />
                        {clientRelationshipGroups.length === 0 ? (
                            noResultsContent
                        ) : (
                            <div className="planning-results-table">
                                <div className="planning-results-grid planning-results-header-row">
                                    <span className="planning-results-header-title">Client Relationship Group</span>
                                    <span>Client Name(s)</span>
                                    <span>ULEK(s)</span>
                                    <span>Status</span>
                                </div>
                                <Accordion>
                                    {clientRelationshipGroups.map((clientRelationshipGroup, index) => (
                                        <AccordionItem
                                            key={clientRelationshipGroup.crgid ?? index}
                                            uuid={clientRelationshipGroup.crgid ?? `planning-group-${index}`}
                                            HeaderComponent={({expanded}) => (
                                                <ClientRelationshipGroupHeaderRow
                                                    expanded={expanded}
                                                    clientRelationshipGroup={clientRelationshipGroup}
                                                    isClientRelationshipGroupLocked={isClientRelationshipGroupLocked}
                                                />
                                            )}
                                        >
                                            <ClientRelationshipGroupDetails
                                                clientRelationshipGroup={clientRelationshipGroup}
                                                partners={partners}
                                                isClientRelationshipGroupLocked={isClientRelationshipGroupLocked}
                                            />
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    )
}
export default ClientRelationshipGroup;